import skillTree from "@/supabase/seed/anuka-eq-skill-tree-v2.json"
import { domainSemanticColors, semanticColor } from "@/theme/emo-coach-theme"
import { Brain, Eye, Handshake, LucideIcon, ShieldCheck } from "lucide-react-native"

export type DomainId =
  | "self_awareness"
  | "self_management"
  | "social_awareness"
  | "relationship_management"

export type SkillStatus = "Completed" | "In Progress" | "Locked"

export type ImmersionLevel = {
  level: number
  name_en?: string
  name_ru?: string
  visible_skill_ids?: string[]
  mastery_unlocked?: boolean
  unlock_requirement_en?: string
}

export type SkillLevel = {
  id: string
  title: string
  description: string
  status: SkillStatus
  action: string
  proofSuggestion: string
  behaviorIndicators: string[]
  coachingPrompts: string[]
}

export type Skill = {
  id: string
  domainId: DomainId
  title: string
  icon: string
  progress: number
  completedLevels: number
  description: string
  whyItMatters: string
  examples: string[]
  levels: SkillLevel[]
  unlockStage: number
  subSkills: string[]
}

export type Domain = {
  id: DomainId
  title: string
  shortTitle: string
  icon: LucideIcon
  emoji: string
  color: string
  progress: number
  completedLevels: number
  totalLevels: number
  skills: Skill[]
  immersionLevels: ImmersionLevel[]
}

type SeedImmersionLevel = {
  level: number
  name_en?: string
  name_ru?: string
  visible_skill_ids?: string[]
  mastery_unlocked?: boolean
  unlock_requirement_en?: string
}

type SeedSkillLevel = {
  level: number
  title_en?: string
  title_ru?: string
  action_en?: string
  action_ru?: string
  proof_suggestion_en?: string
  proof_suggestion_ru?: string
  behavior_indicators?: string[]
  coaching_prompts?: string[]
}

type SeedSkill = {
  id: string
  name_en: string
  name_ru?: string
  icon?: string
  unlock_stage?: number
  sub_skills?: string[]
  description_en?: string
  description_ru?: string
  why_important_en?: string
  why_important_ru?: string
  examples_en?: string[]
  examples_ru?: string[]
  levels: SeedSkillLevel[]
}

type SeedDomain = {
  id: string
  name_en: string
  name_ru?: string
  description_en?: string
  description_ru?: string
  icon?: string
  color?: string
  immersion_levels?: SeedImmersionLevel[]
  skills: SeedSkill[]
}

const seedDomains = skillTree.domains as SeedDomain[]

const domainIcons: Record<DomainId, LucideIcon> = {
  self_awareness: Brain,
  self_management: ShieldCheck,
  social_awareness: Eye,
  relationship_management: Handshake,
}

const domainShortTitles: Record<DomainId, string> = {
  self_awareness: "Awareness",
  self_management: "Management",
  social_awareness: "Social",
  relationship_management: "Relations",
}

const localCompletedLevelsBySkill: Record<string, number> = {
  emotional_self_awareness: 2,
  accurate_self_assessment: 1,
  values_purpose_awareness: 0,
  emotional_patterns_triggers: 0,
  self_regard_compassion: 0,
  needs_boundaries_awareness: 0,
  emotional_self_control: 2,
  stress_tolerance_recovery: 1,
  adaptability_resilience: 0,
  consequential_thinking: 0,
  achievement_orientation: 0,
  intrinsic_motivation_follow_through: 0,
  empathy_perspective_taking: 1,
  active_listening_presence: 2,
  contextual_organizational_awareness: 0,
  nonverbal_emotional_cues: 0,
  service_orientation: 0,
  inclusion_cultural_humility: 0,
  influence_persuasion: 1,
  conflict_management: 1,
  cross_cultural_intelligence: 0,
  trust_repair: 0,
  clear_communication_requests: 0,
  collaborative_leadership: 0,
}

export const legacySkillAliases: Record<string, string> = {
  "emotion-labeling": "emotional_self_awareness",
  "bias-check": "accurate_self_assessment",
  "values-map": "values_purpose_awareness",
  "pause-before-react": "emotional_self_control",
  "stress-reset": "stress_tolerance_recovery",
  "commitment-follow-through": "intrinsic_motivation_follow_through",
  "active-listening": "active_listening_presence",
  "empathy-signal": "empathy_perspective_taking",
  "context-reading": "contextual_organizational_awareness",
  "clear-request": "clear_communication_requests",
  "conflict-repair": "conflict_management",
  "influence-with-care": "influence_persuasion",
}

export const legacyDomainAliases: Record<string, DomainId> = {
  "self-awareness": "self_awareness",
  "self-management": "self_management",
  "social-awareness": "social_awareness",
  "relationship-management": "relationship_management",
}

export function normalizeSkillId(skillId: string | null | undefined) {
  if (!skillId) return ""
  return legacySkillAliases[skillId] ?? skillId
}

export function normalizeDomainId(domainId: string | null | undefined): DomainId | undefined {
  if (!domainId) return undefined
  return (legacyDomainAliases[domainId] ?? domainId) as DomainId
}

function toDomainId(domainId: string): DomainId {
  return normalizeDomainId(domainId) ?? "self_awareness"
}

function getLocalLevelStatus(
  skillId: string,
  levelNumber: number,
  unlockStage: number,
): SkillStatus {
  const completedLevels = localCompletedLevelsBySkill[skillId] ?? 0
  if (levelNumber <= completedLevels) return "Completed"
  if (completedLevels === 0 && unlockStage > 1) return "Locked"
  if (levelNumber === completedLevels + 1) return "In Progress"
  return "Locked"
}

function buildSkillLevel(seedSkill: SeedSkill, level: SeedSkillLevel): SkillLevel {
  const title = level.title_en ?? level.title_ru ?? "Level " + level.level
  const action = level.action_en ?? level.action_ru ?? "Practice this skill in a real moment."

  return {
    id: "l" + level.level,
    title: "Level " + level.level,
    description: title + ". " + action,
    status: getLocalLevelStatus(seedSkill.id, level.level, seedSkill.unlock_stage ?? 1),
    action,
    proofSuggestion:
      level.proof_suggestion_en ?? level.proof_suggestion_ru ?? "Capture a proof note.",
    behaviorIndicators: level.behavior_indicators ?? [],
    coachingPrompts: level.coaching_prompts ?? [],
  }
}

function buildSkill(seedSkill: SeedSkill, domainId: DomainId): Skill {
  const levels = seedSkill.levels.map((level) => buildSkillLevel(seedSkill, level))
  const completedLevels = levels.filter((level) => level.status === "Completed").length

  return {
    id: seedSkill.id,
    domainId,
    title: seedSkill.name_en,
    icon: seedSkill.icon ?? "✨",
    progress: levels.length > 0 ? Math.round((completedLevels / levels.length) * 100) : 0,
    completedLevels,
    description: seedSkill.description_en ?? seedSkill.description_ru ?? "Practice this EQ skill.",
    whyItMatters:
      seedSkill.why_important_en ??
      seedSkill.why_important_ru ??
      "This skill turns practice into visible growth.",
    examples: seedSkill.examples_en ?? seedSkill.examples_ru ?? [],
    levels,
    unlockStage: seedSkill.unlock_stage ?? 1,
    subSkills: seedSkill.sub_skills ?? [],
  }
}

export const domains: Domain[] = seedDomains.map((seedDomain) => {
  const domainId = toDomainId(seedDomain.id)
  const skills = seedDomain.skills.map((skill) => buildSkill(skill, domainId))
  const totalLevels = skills.reduce((sum, skill) => sum + skill.levels.length, 0)
  const completedLevels = skills.reduce((sum, skill) => sum + skill.completedLevels, 0)

  return {
    id: domainId,
    title: seedDomain.name_en,
    shortTitle: domainShortTitles[domainId],
    icon: domainIcons[domainId],
    emoji: seedDomain.icon ?? "✨",
    color: domainSemanticColors[domainId] ?? seedDomain.color ?? semanticColor("skill"),
    progress: totalLevels > 0 ? Math.round((completedLevels / totalLevels) * 100) : 0,
    completedLevels,
    totalLevels,
    skills,
    immersionLevels: seedDomain.immersion_levels ?? [],
  }
})

export const allSkills = domains.flatMap((domain) => domain.skills)

export const recentProofs = [
  {
    id: "p1",
    title: "Paused before responding",
    skill: "Pause Before React",
    level: "Level 2",
    status: "Verified",
    color: semanticColor("proof"),
  },
  {
    id: "p2",
    title: "Trigger review",
    skill: "Emotion Labeling",
    level: "Level 3",
    status: "EMO.coach review",
    color: semanticColor("proof"),
  },
  {
    id: "p3",
    title: "Clear request",
    skill: "Clear Request",
    level: "Level 2",
    status: "Verified",
    color: semanticColor("proof"),
  },
]

export type TaskStatus = "Active" | "Linked" | "Completed"
export type Visibility = "private" | "group" | "public"

export type GrowthTask = {
  id: string
  title: string
  goal: string
  domainId: DomainId
  skillIds: string[]
  suggestedSkillIds: string[]
  contactId?: string
  checkpoint: string
  due: string
  status: TaskStatus
  completedAt?: string
  nextStep: string
  reflection?: {
    text: string
    videoUrl?: string
    date: string
    visibility: Visibility
  }
}

export type JournalEntry = {
  id: string
  taskId: string
  taskTitle: string
  date: string
  skillIds: string[]
  contactIds?: string[]
  text: string
  videoThumbnail?: string
  visibility: Visibility
}

export type FeedItem = {
  id: string
  person: string
  role: string
  date: string
  title: string
  summary: string
  skillIds: string[]
  videoThumbnail?: string
  visibility: "group" | "public"
  likes: number
  comments: number
}

export type CoachSurface = "home" | "tasks" | "tree" | "journal" | "passport"

export type CoachRecommendation = {
  id: string
  surface: CoachSurface
  title: string
  reason: string
  actionLabel: string
  route: string
  skillIds: string[]
  accentColor: string
  priority: number
}

export type CourseRecommendation = {
  id: string
  skillId: string
  title: string
  provider: string
  duration: string
  certificateTitle: string
  outcome: string
}

export type CertificatePathway = {
  id: string
  skillId: string
  title: string
  currentLevel: number
  targetLevel: number
  proofsRemaining: number
  coursesRemaining: number
  streakLabel: string
  certificateTitle: string
}

export type NetworkContact = {
  id: string
  name: string
  role: string
  avatar?: string
  frequency: string
  relationship: string
  context: string
  skillIds: string[]
  skillTrends?: ContactSkillTrend[]
}

export type ActiveGoal = {
  id: string
  title: string
  context: string
  due: string
  skillIds: string[]
}

export type CoachAction = {
  id: string
  skillId: string
  title: string
  description: string
  contactId?: string
  goalId?: string
  timing: string
  effort: string
  quickTip: string
  mindset: string
  phrases: string[]
  preparation: string[]
  expectedOutcome: string
  taskTitle: string
}

export type ContactMood = "Positive" | "Neutral" | "Mixed"

export type ContactStats = {
  totalInteractions: number
  lastInteracted: string
  lastProof: string
  averageMood: ContactMood
  status: string
}

export type ContactTrendDirection = "positive" | "negative" | "neutral"

export type ContactSkillHistoryPoint = {
  id: string
  date: string
  skillId: string
  score: number
  interactionLabel: string
}

export type ContactSkillTrend = {
  skillId: string
  currentLevel: number
  currentScore: number
  changePercent: number
  direction: ContactTrendDirection
  history: ContactSkillHistoryPoint[]
  insight: string
}

export type ContactGrowthOpportunity = {
  contact: NetworkContact
  primaryTrend: ContactSkillTrend
  recommendation: string
}

export type ContactTimelineItem = {
  id: string
  type: "Goal" | "Task" | "Event"
  title: string
  meta: string
  status?: string
}

export type ContactSuggestedAction = {
  id: string
  title: string
  skillId: string
  advice: string
  coachActionId?: string
}

export type ContactDetails = {
  contact: NetworkContact
  stats: ContactStats
  skillTrends: ContactSkillTrend[]
  insight: string
  practicedSkillIds: string[]
  relatedGoals: ActiveGoal[]
  relatedTasks: GrowthTask[]
  latestConnections: ContactTimelineItem[]
  journalEntries: JournalEntry[]
  suggestedActions: ContactSuggestedAction[]
}

export const tasks: GrowthTask[] = [
  {
    id: "t1",
    title: "Record a 60-second proof after a difficult conversation",
    goal: "Prepare for calmer client conversations",
    domainId: "self_awareness" as DomainId,
    skillIds: ["emotion-labeling", "pause-before-react", "active-listening"],
    suggestedSkillIds: ["conflict-repair", "empathy-signal"],
    contactId: "c-sam",
    checkpoint: "Level 3",
    due: "Today",
    status: "Active",
    nextStep: "Add one sentence that separates the facts from your interpretation.",
  },
  {
    id: "t2",
    title: "Make a clear request for one work task",
    goal: "Reduce team ambiguity this week",
    domainId: "relationship_management" as DomainId,
    skillIds: ["clear-request", "influence-with-care"],
    suggestedSkillIds: ["commitment-follow-through", "context-reading"],
    contactId: "c-nina",
    checkpoint: "Level 3",
    due: "Tomorrow",
    status: "Linked",
    nextStep: "Ask the other person to repeat the agreed action, owner, and deadline.",
  },
  {
    id: "t3",
    title: "Record a stress reset before and after deep work",
    goal: "Protect focus during delivery work",
    domainId: "self_management" as DomainId,
    skillIds: ["stress-reset", "commitment-follow-through"],
    suggestedSkillIds: ["values-map", "bias-check"],
    contactId: "c-kenji",
    checkpoint: "Level 2",
    due: "Fri",
    status: "Active",
    nextStep: "Compare your body signal before and after the reset in one short note.",
  },
  {
    id: "t4",
    title: "Debrief contract negotiation with Client X",
    goal: "Turn a real negotiation into EQ growth",
    domainId: "relationship_management" as DomainId,
    skillIds: ["influence-with-care", "conflict-repair", "clear-request"],
    suggestedSkillIds: ["empathy-signal"],
    contactId: "c-omar",
    checkpoint: "Level 2",
    due: "Today",
    status: "Completed",
    completedAt: "2026-07-04",
    nextStep: "Level up Conflict Repair by documenting the repair move you used.",
    reflection: {
      text: "I stayed calm when the scope changed, named the shared goal, and offered two options instead of pushing one answer.",
      videoUrl: "mock://video/client-x-negotiation",
      date: "2026-07-04",
      visibility: "group",
    },
  },
]

export const developmentJournal: JournalEntry[] = [
  {
    id: "j1",
    taskId: "t4",
    taskTitle: "Debrief contract negotiation with Client X",
    date: "2026-07-04",
    skillIds: ["influence-with-care", "conflict-repair", "clear-request"],
    contactIds: ["c-omar", "c-diego", "c-elena"],
    text: "I stayed calm when the scope changed, named the shared goal, and offered two options instead of pushing one answer.",
    videoThumbnail: "60 sec video proof",
    visibility: "group",
  },
  {
    id: "j2",
    taskId: "t1",
    taskTitle: "Difficult team conversation",
    date: "2026-07-03",
    skillIds: ["emotion-labeling", "active-listening"],
    contactIds: ["c-sam", "c-maya", "c-alex"],
    text: "Before replying, I named irritation and uncertainty. That pause helped me ask a question instead of defending.",
    visibility: "private",
  },
  {
    id: "j3",
    taskId: "t3",
    taskTitle: "Deep work reset",
    date: "2026-07-02",
    skillIds: ["stress-reset", "commitment-follow-through"],
    contactIds: ["c-kenji", "c-anya", "c-max"],
    text: "The two-minute reset lowered tension from 7/10 to 4/10 and I finished the draft without switching tasks.",
    videoThumbnail: "Before / after clip",
    visibility: "private",
  },
]

export const groupFeed: FeedItem[] = [
  {
    id: "f1",
    person: "Maya Chen",
    role: "Growth Circle",
    date: "Today",
    title: "Practiced Clear Request in a roadmap review",
    summary:
      "Maya turned a vague ask into owner, deadline, and success criteria. The team agreed on the next step in one pass.",
    skillIds: ["clear-request", "commitment-follow-through"],
    videoThumbnail: "Roadmap review clip",
    visibility: "group",
    likes: 12,
    comments: 3,
  },
  {
    id: "f2",
    person: "Sergey",
    role: "My shared reflection",
    date: "Today",
    title: "Negotiated scope without pressure",
    summary:
      "Shared the Client X reflection with the circle: calm tone, two options, and a clear repair path.",
    skillIds: ["influence-with-care", "conflict-repair"],
    videoThumbnail: "60 sec reflection",
    visibility: "group",
    likes: 8,
    comments: 2,
  },
  {
    id: "f3",
    person: "Alex Rivera",
    role: "Growth Circle",
    date: "Yesterday",
    title: "Used Active Listening during feedback",
    summary:
      "Alex repeated the feedback in their own words before responding, which changed the conversation from defensive to useful.",
    skillIds: ["active-listening", "empathy-signal"],
    visibility: "group",
    likes: 15,
    comments: 5,
  },
]

export const passportBadges = [
  { id: "b1", title: "First Proof", subtitle: "Genesis badge", color: semanticColor("proof") },
  { id: "b2", title: "Repair Loop", subtitle: "Relationship proof", color: semanticColor("proof") },
  { id: "b3", title: "7 Day Streak", subtitle: "Consistency", color: semanticColor("proof") },
  { id: "b4", title: "Deep Listener", subtitle: "Social awareness", color: semanticColor("proof") },
]

export const coachRecommendations: CoachRecommendation[] = [
  {
    id: "coach-home-proof",
    surface: "home",
    title: "Coach this moment from today’s hard conversation",
    reason:
      "Coach Mode sees active progress in Emotion Labeling and Pause Before React. Start with one small action, then log proof after you do it.",
    actionLabel: "+ Coach this moment",
    route: "/coach/emotion-labeling",
    skillIds: ["emotion-labeling", "pause-before-react"],
    accentColor: semanticColor("coach"),
    priority: 1,
  },
  {
    id: "coach-home-course",
    surface: "home",
    title: "Start Conflict Repair foundations",
    reason:
      "Your completed negotiation task already touched repair. A short course would turn that moment into a clearer certificate path.",
    actionLabel: "Open skill",
    route: "/skill/conflict-repair",
    skillIds: ["conflict-repair"],
    accentColor: semanticColor("skill"),
    priority: 2,
  },
  {
    id: "coach-tasks-proof",
    surface: "tasks",
    title: "Finish one linked task, then write the reflection while it is fresh",
    reason:
      "Tasks with skill tags become Passport evidence only after a reflection, video, or proof note is attached.",
    actionLabel: "Open journal",
    route: "/journal",
    skillIds: ["commitment-follow-through", "active-listening"],
    accentColor: semanticColor("growth"),
    priority: 1,
  },
  {
    id: "coach-tree-focus",
    surface: "tree",
    title: "Focus next on Conflict Repair",
    reason:
      "Relationship Management has real task evidence but only one completed Conflict Repair level. This is the strongest next growth branch.",
    actionLabel: "Open skill",
    route: "/skill/conflict-repair",
    skillIds: ["conflict-repair", "influence-with-care"],
    accentColor: semanticColor("skill"),
    priority: 1,
  },
  {
    id: "coach-journal-prompt",
    surface: "journal",
    title: "Use the repair prompt for your next reflection",
    reason:
      "The best next journal entry should connect what happened, what you changed, and what you will try in the next conversation.",
    actionLabel: "Log proof",
    route: "/proof/new",
    skillIds: ["conflict-repair", "empathy-signal"],
    accentColor: semanticColor("proof"),
    priority: 1,
  },
  {
    id: "coach-passport-path",
    surface: "passport",
    title: "Push Influence With Care toward Level 4",
    reason:
      "You already have negotiation evidence. Three more proofs plus one course can unlock a stronger external-facing certificate preview.",
    actionLabel: "Open pathway",
    route: "/skill/influence-with-care",
    skillIds: ["influence-with-care", "clear-request"],
    accentColor: semanticColor("proof"),
    priority: 1,
  },
]

export const courseRecommendations: CourseRecommendation[] = [
  {
    id: "course-conflict-repair",
    skillId: "conflict-repair",
    title: "Conflict Repair Foundations",
    provider: "Coursera preview",
    duration: "4 hours",
    certificateTitle: "Repair Conversation Certificate",
    outcome: "Practice apology, impact naming, and next-agreement design.",
  },
  {
    id: "course-influence-care",
    skillId: "influence-with-care",
    title: "Advanced Negotiation With Care",
    provider: "MasterClass preview",
    duration: "6 hours",
    certificateTitle: "Negotiation Mastery Certificate",
    outcome: "Turn influence into shared options without pressure.",
  },
  {
    id: "course-active-listening",
    skillId: "active-listening",
    title: "Deep Listening for Feedback",
    provider: "LinkedIn Learning preview",
    duration: "2.5 hours",
    certificateTitle: "Deep Listener Certificate",
    outcome: "Use paraphrase, emotion check, and curiosity loops in feedback.",
  },
  {
    id: "course-stress-reset",
    skillId: "stress-reset",
    title: "Nervous System Reset at Work",
    provider: "Calm Business preview",
    duration: "90 minutes",
    certificateTitle: "Stress Reset Badge",
    outcome: "Build a repeatable reset before high-stakes work.",
  },
]

export const certificatePathways: CertificatePathway[] = [
  {
    id: "path-influence",
    skillId: "influence-with-care",
    title: "Ethical Influence Path",
    currentLevel: 2,
    targetLevel: 4,
    proofsRemaining: 3,
    coursesRemaining: 1,
    streakLabel: "2 of 5 practice days",
    certificateTitle: "Negotiation Mastery Certificate",
  },
  {
    id: "path-conflict",
    skillId: "conflict-repair",
    title: "Repair Loop Path",
    currentLevel: 2,
    targetLevel: 4,
    proofsRemaining: 3,
    coursesRemaining: 1,
    streakLabel: "1 repair proof logged",
    certificateTitle: "Repair Conversation Certificate",
  },
  {
    id: "path-listening",
    skillId: "active-listening",
    title: "Deep Listener Path",
    currentLevel: 3,
    targetLevel: 4,
    proofsRemaining: 2,
    coursesRemaining: 0,
    streakLabel: "Feedback proof pending",
    certificateTitle: "Deep Listener Certificate",
  },
]

const rawMockContacts: NetworkContact[] = [
  {
    id: "c-maya",
    name: "Maya Chen",
    role: "Product partner",
    frequency: "2-3 times / week",
    relationship: "Close collaborator",
    context: "Roadmap tradeoffs, feedback, and priority alignment.",
    skillIds: ["active-listening", "clear-request", "conflict-repair"],
  },
  {
    id: "c-alex",
    name: "Alex Rivera",
    role: "Design lead",
    frequency: "Weekly",
    relationship: "Creative peer",
    context: "Design critique, scope tension, and team rituals.",
    skillIds: ["empathy-signal", "context-reading", "active-listening"],
  },
  {
    id: "c-nina",
    name: "Nina Patel",
    role: "Engineering manager",
    frequency: "Daily",
    relationship: "Delivery partner",
    context: "Execution risk, commitments, and technical prioritization.",
    skillIds: ["commitment-follow-through", "clear-request", "pause-before-react"],
  },
  {
    id: "c-omar",
    name: "Omar Haddad",
    role: "Client sponsor",
    frequency: "Every 2 weeks",
    relationship: "External stakeholder",
    context: "Contract scope, expectations, and renewal conversations.",
    skillIds: ["influence-with-care", "clear-request", "conflict-repair"],
  },
  {
    id: "c-lena",
    name: "Lena Volkova",
    role: "Founder mentor",
    frequency: "Monthly",
    relationship: "Trusted advisor",
    context: "Strategy choices, blind spots, and career decisions.",
    skillIds: ["bias-check", "values-map", "context-reading"],
  },
  {
    id: "c-diego",
    name: "Diego Morales",
    role: "Sales lead",
    frequency: "Weekly",
    relationship: "Revenue partner",
    context: "Deal reviews, objections, and handoff quality.",
    skillIds: ["influence-with-care", "active-listening", "clear-request"],
  },
  {
    id: "c-anya",
    name: "Anya Petrova",
    role: "Operations lead",
    frequency: "Daily",
    relationship: "Execution partner",
    context: "Process friction, deadlines, and accountability loops.",
    skillIds: ["commitment-follow-through", "stress-reset", "clear-request"],
  },
  {
    id: "c-sam",
    name: "Sam Okafor",
    role: "Teammate",
    frequency: "2 times / week",
    relationship: "Growth circle",
    context: "Feedback practice, difficult messages, and follow-ups.",
    skillIds: ["emotion-labeling", "empathy-signal", "conflict-repair"],
  },
  {
    id: "c-irina",
    name: "Irina Sokolova",
    role: "Recruiting partner",
    frequency: "Monthly",
    relationship: "Professional network",
    context: "Hiring calibration, candidate feedback, and role clarity.",
    skillIds: ["bias-check", "active-listening", "context-reading"],
  },
  {
    id: "c-jordan",
    name: "Jordan Lee",
    role: "Investor advisor",
    frequency: "Monthly",
    relationship: "Strategic reviewer",
    context: "Narrative pressure, high-stakes questions, and decision quality.",
    skillIds: ["pause-before-react", "values-map", "influence-with-care"],
  },
  {
    id: "c-sofia",
    name: "Sofia Garcia",
    role: "Customer success lead",
    frequency: "Weekly",
    relationship: "Customer voice",
    context: "Customer emotion, churn risk, and repair opportunities.",
    skillIds: ["empathy-signal", "context-reading", "conflict-repair"],
  },
  {
    id: "c-kenji",
    name: "Kenji Tanaka",
    role: "Technical lead",
    frequency: "Daily",
    relationship: "Implementation partner",
    context: "Tradeoffs, estimation, and calm delivery under pressure.",
    skillIds: ["stress-reset", "clear-request", "commitment-follow-through"],
  },
  {
    id: "c-priya",
    name: "Priya Shah",
    role: "People partner",
    frequency: "Every 2 weeks",
    relationship: "Culture partner",
    context: "Team health, conflict repair, and feedback systems.",
    skillIds: ["empathy-signal", "conflict-repair", "active-listening"],
  },
  {
    id: "c-max",
    name: "Max Ivanov",
    role: "Peer founder",
    frequency: "Weekly",
    relationship: "Accountability partner",
    context: "Commitments, values, and founder stress.",
    skillIds: ["values-map", "stress-reset", "commitment-follow-through"],
  },
  {
    id: "c-elena",
    name: "Elena Smirnova",
    role: "Key customer",
    frequency: "Monthly",
    relationship: "Customer champion",
    context: "Product feedback, unmet needs, and trust building.",
    skillIds: ["active-listening", "empathy-signal", "clear-request"],
  },
]

const trendDates = ["Jun 9", "Jun 16", "Jun 23", "Jun 30", "Jul 4"]

const contactSkillScoresById: Record<string, Record<string, number[]>> = {
  "c-maya": {
    "active-listening": [58, 61, 67, 72, 78],
    "clear-request": [52, 57, 61, 66, 73],
    "conflict-repair": [48, 46, 51, 55, 58],
  },
  "c-alex": {
    "empathy-signal": [62, 60, 57, 55, 54],
    "context-reading": [51, 56, 60, 63, 66],
    "active-listening": [65, 64, 62, 61, 60],
  },
  "c-nina": {
    "commitment-follow-through": [55, 59, 64, 70, 76],
    "clear-request": [48, 54, 60, 66, 72],
    "pause-before-react": [42, 47, 52, 55, 59],
  },
  "c-omar": {
    "influence-with-care": [50, 57, 63, 68, 72],
    "clear-request": [58, 56, 54, 52, 50],
    "conflict-repair": [48, 45, 43, 42, 40],
  },
  "c-lena": {
    "bias-check": [54, 58, 61, 64, 68],
    "values-map": [70, 70, 71, 72, 72],
    "context-reading": [59, 62, 64, 67, 69],
  },
  "c-diego": {
    "influence-with-care": [44, 50, 56, 63, 69],
    "active-listening": [61, 59, 58, 56, 55],
    "clear-request": [47, 53, 55, 60, 64],
  },
  "c-anya": {
    "commitment-follow-through": [63, 66, 70, 73, 78],
    "stress-reset": [52, 49, 47, 45, 44],
    "clear-request": [55, 58, 60, 62, 63],
  },
  "c-sam": {
    "emotion-labeling": [46, 52, 58, 64, 71],
    "empathy-signal": [55, 59, 62, 66, 70],
    "conflict-repair": [50, 48, 46, 45, 44],
  },
  "c-irina": {
    "bias-check": [49, 53, 56, 60, 63],
    "active-listening": [52, 51, 51, 52, 53],
    "context-reading": [46, 50, 54, 57, 61],
  },
  "c-jordan": {
    "pause-before-react": [43, 47, 51, 56, 60],
    "values-map": [64, 65, 65, 66, 66],
    "influence-with-care": [60, 58, 55, 53, 51],
  },
  "c-sofia": {
    "empathy-signal": [57, 62, 67, 71, 75],
    "context-reading": [50, 56, 61, 65, 70],
    "conflict-repair": [52, 54, 55, 55, 56],
  },
  "c-kenji": {
    "stress-reset": [48, 55, 61, 67, 73],
    "clear-request": [54, 58, 63, 67, 70],
    "commitment-follow-through": [65, 67, 69, 70, 72],
  },
  "c-priya": {
    "empathy-signal": [61, 64, 66, 69, 72],
    "conflict-repair": [47, 50, 52, 55, 59],
    "active-listening": [68, 67, 67, 68, 69],
  },
  "c-max": {
    "values-map": [62, 65, 68, 72, 76],
    "stress-reset": [55, 53, 50, 48, 47],
    "commitment-follow-through": [58, 62, 65, 69, 71],
  },
  "c-elena": {
    "active-listening": [53, 58, 63, 68, 74],
    "empathy-signal": [49, 54, 58, 63, 67],
    "clear-request": [61, 60, 58, 57, 56],
  },
}

function getTrendDirection(changePercent: number): ContactTrendDirection {
  if (changePercent >= 4) return "positive"
  if (changePercent <= -4) return "negative"
  return "neutral"
}

function buildContactSkillTrends(contact: Pick<NetworkContact, "id" | "skillIds">) {
  const scoreMap = contactSkillScoresById[contact.id] ?? {}

  return contact.skillIds.map((skillId) => {
    const scores = scoreMap[skillId] ?? [48, 51, 53, 55, 57]
    const history = scores.map((score, index) => ({
      id: `${contact.id}-${skillId}-${index + 1}`,
      date: trendDates[index] ?? `Touch ${index + 1}`,
      skillId,
      score,
      interactionLabel: `Interaction ${index + 1}`,
    }))
    const firstScore = scores[0] ?? 0
    const currentScore = scores[scores.length - 1] ?? firstScore
    const changePercent = currentScore - firstScore
    const direction = getTrendDirection(changePercent)
    const skill = getSkill(skillId)
    const directionLabel =
      direction === "positive" ? "improved" : direction === "negative" ? "declined" : "held steady"

    return {
      skillId,
      currentLevel: Math.max(1, Math.min(4, Math.ceil(currentScore / 25))),
      currentScore,
      changePercent,
      direction,
      history,
      insight: `${skill.title} ${directionLabel} ${changePercent > 0 ? "+" : ""}${changePercent}% across the last ${history.length} interactions.`,
    }
  })
}

export const mockContacts: NetworkContact[] = rawMockContacts.map((contact) => ({
  ...contact,
  skillTrends: buildContactSkillTrends(contact),
}))

export const activeGoals: ActiveGoal[] = [
  {
    id: "g-client-calm",
    title: "Prepare for calmer client conversations",
    context: "Reduce defensiveness and keep renewal conversations constructive.",
    due: "This week",
    skillIds: ["emotion-labeling", "pause-before-react", "conflict-repair"],
  },
  {
    id: "g-team-clarity",
    title: "Reduce team ambiguity this week",
    context: "Turn vague asks into clear owners, deadlines, and expectations.",
    due: "Friday",
    skillIds: ["clear-request", "commitment-follow-through", "active-listening"],
  },
  {
    id: "g-delivery-focus",
    title: "Protect focus during delivery work",
    context: "Lower stress spikes before deep work and high-pressure replies.",
    due: "Next 7 days",
    skillIds: ["stress-reset", "pause-before-react", "values-map"],
  },
  {
    id: "g-negotiation-trust",
    title: "Turn negotiation into trust",
    context: "Influence without pressure while preserving the relationship.",
    due: "Next client review",
    skillIds: ["influence-with-care", "clear-request", "context-reading"],
  },
  {
    id: "g-feedback-loop",
    title: "Build a cleaner feedback loop",
    context: "Listen fully, name what matters, and close repair gaps faster.",
    due: "This month",
    skillIds: ["active-listening", "empathy-signal", "conflict-repair"],
  },
]

const contactStatsById: Record<string, ContactStats> = {
  "c-maya": {
    totalInteractions: 18,
    lastInteracted: "1 day ago",
    lastProof: "3 days ago",
    averageMood: "Positive",
    status: "Active connection",
  },
  "c-alex": {
    totalInteractions: 14,
    lastInteracted: "2 days ago",
    lastProof: "6 days ago",
    averageMood: "Mixed",
    status: "Last interacted 2 days ago",
  },
  "c-nina": {
    totalInteractions: 22,
    lastInteracted: "Today",
    lastProof: "4 days ago",
    averageMood: "Neutral",
    status: "Active connection",
  },
  "c-omar": {
    totalInteractions: 12,
    lastInteracted: "3 days ago",
    lastProof: "5 days ago",
    averageMood: "Positive",
    status: "Last interacted 3 days ago",
  },
  "c-lena": {
    totalInteractions: 9,
    lastInteracted: "6 days ago",
    lastProof: "2 weeks ago",
    averageMood: "Positive",
    status: "Mentor check-in due soon",
  },
  "c-diego": {
    totalInteractions: 11,
    lastInteracted: "4 days ago",
    lastProof: "1 week ago",
    averageMood: "Neutral",
    status: "Last interacted 4 days ago",
  },
  "c-anya": {
    totalInteractions: 16,
    lastInteracted: "Today",
    lastProof: "2 days ago",
    averageMood: "Mixed",
    status: "Active connection",
  },
  "c-sam": {
    totalInteractions: 10,
    lastInteracted: "Yesterday",
    lastProof: "Yesterday",
    averageMood: "Positive",
    status: "Active connection",
  },
  "c-irina": {
    totalInteractions: 7,
    lastInteracted: "8 days ago",
    lastProof: "No proof yet",
    averageMood: "Neutral",
    status: "Hiring calibration follow-up",
  },
  "c-jordan": {
    totalInteractions: 8,
    lastInteracted: "1 week ago",
    lastProof: "2 weeks ago",
    averageMood: "Mixed",
    status: "Advisor loop open",
  },
  "c-sofia": {
    totalInteractions: 13,
    lastInteracted: "2 days ago",
    lastProof: "4 days ago",
    averageMood: "Positive",
    status: "Active connection",
  },
  "c-kenji": {
    totalInteractions: 20,
    lastInteracted: "Today",
    lastProof: "2 days ago",
    averageMood: "Positive",
    status: "Active connection",
  },
  "c-priya": {
    totalInteractions: 9,
    lastInteracted: "5 days ago",
    lastProof: "1 week ago",
    averageMood: "Positive",
    status: "Feedback system partner",
  },
  "c-max": {
    totalInteractions: 15,
    lastInteracted: "Yesterday",
    lastProof: "2 days ago",
    averageMood: "Mixed",
    status: "Active connection",
  },
  "c-elena": {
    totalInteractions: 8,
    lastInteracted: "4 days ago",
    lastProof: "5 days ago",
    averageMood: "Positive",
    status: "Customer follow-up open",
  },
}

const contactEventsById: Record<string, ContactTimelineItem[]> = {
  "c-maya": [
    { id: "event-maya-1", type: "Event", title: "Roadmap review", meta: "July 3" },
    {
      id: "event-maya-2",
      type: "Task",
      title: "Write planning recap",
      meta: "Completed yesterday",
    },
  ],
  "c-alex": [
    { id: "event-alex-1", type: "Event", title: "Design critique", meta: "July 2" },
    {
      id: "event-alex-2",
      type: "Task",
      title: "Summarize critique neutrally",
      meta: "Due next critique",
    },
  ],
  "c-omar": [
    { id: "event-omar-1", type: "Goal", title: "Negotiate contract renewal", meta: "Linked" },
    { id: "event-omar-2", type: "Event", title: "Client call", meta: "July 2" },
    {
      id: "event-omar-3",
      type: "Task",
      title: "Prepare feedback for Omar",
      meta: "Completed 2 days ago",
      status: "Completed",
    },
  ],
  "c-sam": [
    { id: "event-sam-1", type: "Task", title: "Repair follow-up after tension", meta: "Today" },
    { id: "event-sam-2", type: "Event", title: "Growth circle check-in", meta: "Yesterday" },
  ],
}

type CoachActionBrief = {
  title: string
  description: string
  contactId?: string
  goalId?: string
  timing: string
  effort: string
  quickTip: string
}

const coachActionBriefs: Record<string, CoachActionBrief[]> = {
  "emotion-labeling": [
    {
      title: "Name the emotion before the next reply",
      description: "Pause before answering Sam and write the feeling, body signal, and trigger.",
      contactId: "c-sam",
      goalId: "g-client-calm",
      timing: "Before your next message",
      effort: "3 minutes",
      quickTip: "Use one precise word instead of a broad label like bad or stressed.",
    },
    {
      title: "Do a fact-versus-story check",
      description: "Separate what happened from the interpretation you are carrying into the call.",
      contactId: "c-omar",
      goalId: "g-client-calm",
      timing: "Before the client sync",
      effort: "5 minutes",
      quickTip: "Write one sentence for facts and one sentence for the story your mind added.",
    },
    {
      title: "Ask for an emotion mirror",
      description: "Invite Maya to reflect what emotion she hears in your summary.",
      contactId: "c-maya",
      goalId: "g-feedback-loop",
      timing: "During your next 1:1",
      effort: "8 minutes",
      quickTip: "Make it lightweight: 'What emotion do you hear under this?'",
    },
    {
      title: "Record a 60-second trigger note",
      description: "Capture the moment while it is fresh, then log it as proof if useful.",
      contactId: "c-max",
      goalId: "g-delivery-focus",
      timing: "Today",
      effort: "6 minutes",
      quickTip: "Name the trigger without judging the reaction.",
    },
  ],
  "bias-check": [
    {
      title: "Ask for the missing data point",
      description: "Use Irina to test one assumption you may be over-weighting.",
      contactId: "c-irina",
      goalId: "g-feedback-loop",
      timing: "Before the hiring decision",
      effort: "10 minutes",
      quickTip: "Ask what evidence would change your mind.",
    },
    {
      title: "Run a contrary-read review",
      description: "Ask Lena to argue the strongest case against your preferred decision.",
      contactId: "c-lena",
      goalId: "g-negotiation-trust",
      timing: "This week",
      effort: "15 minutes",
      quickTip: "Listen for the assumption, not the conclusion.",
    },
    {
      title: "Check the first impression",
      description: "Compare your first read of Alex's feedback with two observable facts.",
      contactId: "c-alex",
      goalId: "g-feedback-loop",
      timing: "After the critique",
      effort: "5 minutes",
      quickTip: "Write 'I know' versus 'I infer' as two separate lines.",
    },
    {
      title: "Invite a customer counterexample",
      description: "Ask Elena for one example that does not fit your current product story.",
      contactId: "c-elena",
      goalId: "g-negotiation-trust",
      timing: "Next customer call",
      effort: "10 minutes",
      quickTip: "Thank the counterexample before explaining anything.",
    },
  ],
  "values-map": [
    {
      title: "Tie the decision to one value",
      description: "Ask Max which value your current commitment is protecting or violating.",
      contactId: "c-max",
      goalId: "g-delivery-focus",
      timing: "Today",
      effort: "8 minutes",
      quickTip: "Choose one value, not a list.",
    },
    {
      title: "Name the tradeoff out loud",
      description: "Tell Jordan what value you are willing to protect even if speed suffers.",
      contactId: "c-jordan",
      goalId: "g-negotiation-trust",
      timing: "Before the advisor call",
      effort: "10 minutes",
      quickTip: "Use 'I am choosing X because Y matters here.'",
    },
    {
      title: "Audit the calendar against values",
      description: "Review one week with Lena and mark one activity to stop or shrink.",
      contactId: "c-lena",
      goalId: "g-delivery-focus",
      timing: "This weekend",
      effort: "15 minutes",
      quickTip: "The proof is one calendar change, not a perfect manifesto.",
    },
    {
      title: "Explain the value behind a no",
      description: "Practice a respectful no with Maya that preserves trust and focus.",
      contactId: "c-maya",
      goalId: "g-team-clarity",
      timing: "Next planning discussion",
      effort: "7 minutes",
      quickTip: "Pair the no with the value and the alternative you can support.",
    },
  ],
  "pause-before-react": [
    {
      title: "Draft, breathe, then send",
      description: "Write the reply to Nina, take three breaths, and edit for the result you want.",
      contactId: "c-nina",
      goalId: "g-team-clarity",
      timing: "Before the next delivery update",
      effort: "4 minutes",
      quickTip: "Delete one sentence that tries to win instead of clarify.",
    },
    {
      title: "Use a 10-second call pause",
      description: "When Omar pushes scope, pause and name the shared outcome before responding.",
      contactId: "c-omar",
      goalId: "g-client-calm",
      timing: "Next client review",
      effort: "10 seconds",
      quickTip: "Silence is allowed when it protects the relationship.",
    },
    {
      title: "Ask one question before defending",
      description: "In the critique with Alex, respond first with a curiosity question.",
      contactId: "c-alex",
      goalId: "g-feedback-loop",
      timing: "Next critique",
      effort: "2 minutes",
      quickTip: "Start with 'Can you say more about the impact?'",
    },
    {
      title: "Build a pressure script",
      description: "Prepare one calm sentence for Jordan's hardest advisor question.",
      contactId: "c-jordan",
      goalId: "g-negotiation-trust",
      timing: "Before the advisor sync",
      effort: "6 minutes",
      quickTip: "Slow the tempo before you answer the content.",
    },
  ],
  "stress-reset": [
    {
      title: "Two-minute reset before deep work",
      description: "Reset with Kenji before opening the technical tradeoff thread.",
      contactId: "c-kenji",
      goalId: "g-delivery-focus",
      timing: "Before deep work",
      effort: "2 minutes",
      quickTip: "Rate tension before and after so the proof is visible.",
    },
    {
      title: "Walk before the difficult reply",
      description: "Take one short walk before answering Anya's escalation message.",
      contactId: "c-anya",
      goalId: "g-delivery-focus",
      timing: "Today",
      effort: "7 minutes",
      quickTip: "Do not solve while walking; just lower the charge.",
    },
    {
      title: "Create a reset cue",
      description: "Ask Max to send a simple cue before your next founder check-in.",
      contactId: "c-max",
      goalId: "g-client-calm",
      timing: "Next check-in",
      effort: "3 minutes",
      quickTip: "Use a physical cue you can repeat later.",
    },
    {
      title: "End the call with a nervous-system note",
      description: "After the customer call, tell Sofia what changed in your body state.",
      contactId: "c-sofia",
      goalId: "g-feedback-loop",
      timing: "After the call",
      effort: "5 minutes",
      quickTip: "Keep it observable: shoulders, breathing, jaw, energy.",
    },
  ],
  "commitment-follow-through": [
    {
      title: "Close one tiny loop publicly",
      description: "Tell Anya the next step, owner, and time you will report back.",
      contactId: "c-anya",
      goalId: "g-team-clarity",
      timing: "Today",
      effort: "5 minutes",
      quickTip: "Make the commitment smaller than your confidence.",
    },
    {
      title: "Send a proof-of-done update",
      description: "Send Nina one concrete artifact that shows the task moved.",
      contactId: "c-nina",
      goalId: "g-team-clarity",
      timing: "End of day",
      effort: "6 minutes",
      quickTip: "Evidence beats a status adjective.",
    },
    {
      title: "Ask for accountability wording",
      description: "Have Max rewrite your commitment until it is observable.",
      contactId: "c-max",
      goalId: "g-delivery-focus",
      timing: "This week",
      effort: "10 minutes",
      quickTip: "If a third party cannot verify it, tighten the wording.",
    },
    {
      title: "Turn a promise into a calendar block",
      description: "With Kenji, reserve time for the exact delivery step you promised.",
      contactId: "c-kenji",
      goalId: "g-delivery-focus",
      timing: "Before tomorrow",
      effort: "4 minutes",
      quickTip: "A commitment without time is still a wish.",
    },
  ],
  "active-listening": [
    {
      title: "Reflect before advising",
      description: "In Maya's next update, repeat the core concern before offering a solution.",
      contactId: "c-maya",
      goalId: "g-feedback-loop",
      timing: "Next 1:1",
      effort: "8 minutes",
      quickTip: "Ask 'Did I get that right?' before moving on.",
    },
    {
      title: "Listen for the unsaid constraint",
      description: "Ask Elena what makes the current product feedback hard to say directly.",
      contactId: "c-elena",
      goalId: "g-negotiation-trust",
      timing: "Next customer call",
      effort: "10 minutes",
      quickTip: "Leave room after the question. Do not fill the silence.",
    },
    {
      title: "Summarize the critique",
      description: "After Alex gives design feedback, summarize it in one neutral paragraph.",
      contactId: "c-alex",
      goalId: "g-feedback-loop",
      timing: "After critique",
      effort: "5 minutes",
      quickTip: "Neutral means no defense hidden inside the summary.",
    },
    {
      title: "Ask the second question",
      description:
        "With Priya, ask one follow-up after the first answer instead of switching topics.",
      contactId: "c-priya",
      goalId: "g-feedback-loop",
      timing: "This week",
      effort: "7 minutes",
      quickTip: "The second question is where trust usually starts.",
    },
  ],
  "empathy-signal": [
    {
      title: "Name the hard part",
      description:
        "Tell Sofia what might be frustrating from the customer's side before fixing it.",
      contactId: "c-sofia",
      goalId: "g-feedback-loop",
      timing: "Before the support review",
      effort: "5 minutes",
      quickTip: "Empathy first, explanation second.",
    },
    {
      title: "Offer one concrete support move",
      description: "Ask Priya which support action would actually help the team this week.",
      contactId: "c-priya",
      goalId: "g-feedback-loop",
      timing: "This week",
      effort: "8 minutes",
      quickTip: "Offer one useful action, not general availability.",
    },
    {
      title: "Validate before disagreeing",
      description: "When Alex pushes back, validate the concern before sharing your alternative.",
      contactId: "c-alex",
      goalId: "g-team-clarity",
      timing: "Next critique",
      effort: "4 minutes",
      quickTip: "Validation does not require agreement.",
    },
    {
      title: "Send a care signal after tension",
      description:
        "Follow up with Sam after a difficult exchange and name the relationship you want.",
      contactId: "c-sam",
      goalId: "g-client-calm",
      timing: "After the exchange",
      effort: "6 minutes",
      quickTip: "Keep it brief and specific.",
    },
  ],
  "context-reading": [
    {
      title: "Map the room before speaking",
      description: "Before the roadmap discussion, list each person's pressure and priority.",
      contactId: "c-maya",
      goalId: "g-team-clarity",
      timing: "Before planning",
      effort: "8 minutes",
      quickTip: "Context is not excuse-making; it is better targeting.",
    },
    {
      title: "Ask what changed recently",
      description: "Ask Sofia what changed for the customer before interpreting the feedback.",
      contactId: "c-sofia",
      goalId: "g-feedback-loop",
      timing: "Next customer sync",
      effort: "6 minutes",
      quickTip: "Recent context often explains sudden emotion.",
    },
    {
      title: "Check power and incentives",
      description: "With Lena, identify who carries risk if the decision goes wrong.",
      contactId: "c-lena",
      goalId: "g-negotiation-trust",
      timing: "This week",
      effort: "12 minutes",
      quickTip: "Look at incentives before judging behavior.",
    },
    {
      title: "Read the delivery constraint",
      description: "Ask Kenji what hidden constraint is shaping the technical response.",
      contactId: "c-kenji",
      goalId: "g-delivery-focus",
      timing: "Before the tradeoff call",
      effort: "7 minutes",
      quickTip: "The goal is a better next move, not perfect analysis.",
    },
  ],
  "clear-request": [
    {
      title: "Convert a vague ask into one request",
      description: "Ask Nina for one owner, one deadline, and one done condition.",
      contactId: "c-nina",
      goalId: "g-team-clarity",
      timing: "Today",
      effort: "5 minutes",
      quickTip: "A clear request has action, owner, time, and confirmation.",
    },
    {
      title: "Ask the client for a decision path",
      description: "With Omar, clarify who decides and what information they need.",
      contactId: "c-omar",
      goalId: "g-negotiation-trust",
      timing: "Next client call",
      effort: "10 minutes",
      quickTip: "Make the decision process visible before asking for yes.",
    },
    {
      title: "Request a sharper critique",
      description: "Ask Alex for the one change that would most improve the current design.",
      contactId: "c-alex",
      goalId: "g-feedback-loop",
      timing: "Next critique",
      effort: "4 minutes",
      quickTip: "One request keeps feedback actionable.",
    },
    {
      title: "Close the loop in writing",
      description: "Send Maya a short recap of agreed action, owner, and next checkpoint.",
      contactId: "c-maya",
      goalId: "g-team-clarity",
      timing: "After planning",
      effort: "6 minutes",
      quickTip: "Ask for correction, not approval.",
    },
  ],
  "conflict-repair": [
    {
      title: "Own one part of the tension",
      description: "Tell Sam the specific part you contributed without explaining it away.",
      contactId: "c-sam",
      goalId: "g-client-calm",
      timing: "Today",
      effort: "8 minutes",
      quickTip: "Repair starts with ownership, not a perfect apology.",
    },
    {
      title: "Name impact before intent",
      description:
        "With Omar, acknowledge the impact of the missed expectation before your rationale.",
      contactId: "c-omar",
      goalId: "g-negotiation-trust",
      timing: "Next client touchpoint",
      effort: "10 minutes",
      quickTip: "Intent can come later; impact has to land first.",
    },
    {
      title: "Agree on a new protocol",
      description: "Ask Priya to help turn repeated tension into a simple working agreement.",
      contactId: "c-priya",
      goalId: "g-feedback-loop",
      timing: "This week",
      effort: "15 minutes",
      quickTip: "A repair is stronger when it changes the next interaction.",
    },
    {
      title: "Send a repair follow-up",
      description:
        "After a hard exchange with Maya, write a short message naming what will change.",
      contactId: "c-maya",
      goalId: "g-team-clarity",
      timing: "After the meeting",
      effort: "6 minutes",
      quickTip: "End with the next observable behavior.",
    },
  ],
  "influence-with-care": [
    {
      title: "Lead with shared interest",
      description: "Before proposing a path to Diego, name what both sides are trying to protect.",
      contactId: "c-diego",
      goalId: "g-negotiation-trust",
      timing: "Before deal review",
      effort: "7 minutes",
      quickTip: "Influence lands cleaner when the shared interest is explicit.",
    },
    {
      title: "Offer two options without pressure",
      description: "Give Omar two viable choices and leave space for a no.",
      contactId: "c-omar",
      goalId: "g-negotiation-trust",
      timing: "Next client review",
      effort: "10 minutes",
      quickTip: "Autonomy is part of the persuasion.",
    },
    {
      title: "Check interest before pitching",
      description: "Ask Jordan if this is the right moment for a recommendation before making it.",
      contactId: "c-jordan",
      goalId: "g-client-calm",
      timing: "Advisor sync",
      effort: "3 minutes",
      quickTip: "Consent to influence changes the tone.",
    },
    {
      title: "Translate value for the other side",
      description: "With Elena, frame the proposal through the customer's actual constraint.",
      contactId: "c-elena",
      goalId: "g-feedback-loop",
      timing: "Next customer call",
      effort: "8 minutes",
      quickTip: "Use their language before your argument.",
    },
  ],
}

function buildCoachAction(skillId: string, brief: CoachActionBrief, index: number): CoachAction {
  const canonicalSkillId = normalizeSkillId(skillId)
  const skill = getSkill(canonicalSkillId)
  const titleVerb = brief.title.toLowerCase()

  return {
    id: `${skillId}-action-${index + 1}`,
    skillId: canonicalSkillId,
    ...brief,
    mindset: `Treat this as a small ${skill.title} rep, not a performance. The win is doing one real action with attention.`,
    phrases: [
      `I want to practice ${skill.title} here, so let me slow down for a moment.`,
      "Here is what I am noticing, and here is the specific next step I am asking for.",
      "Tell me what I am missing before we decide.",
    ],
    preparation: [
      `Review the current ${skill.title} checkpoint and choose one observable behavior.`,
      "Write the desired outcome in one sentence before you contact the person.",
      "After the interaction, capture one proof note while the details are still fresh.",
    ],
    expectedOutcome: `You complete one real-world ${skill.title} action and have a clean note that can become a task, proof, or Passport evidence.`,
    taskTitle: `${skill.title}: ${titleVerb}`,
  }
}

export const coachActions: CoachAction[] = Object.entries(coachActionBriefs).flatMap(
  ([skillId, briefs]) => briefs.map((brief, index) => buildCoachAction(skillId, brief, index)),
)

export function getDomain(domainId: DomainId | string | null | undefined) {
  const canonicalDomainId = normalizeDomainId(domainId)
  return domains.find((domain) => domain.id === canonicalDomainId)
}

export function getSkill(skillId: string) {
  const canonicalSkillId = normalizeSkillId(skillId)
  return allSkills.find((skill) => skill.id === canonicalSkillId) ?? allSkills[0]
}

export function getSkills(skillIds: string[]) {
  return skillIds.map((skillId) => getSkill(skillId))
}

export function getSkillDomainColor(skillId: string) {
  const skill = getSkill(skillId)
  return getDomain(skill.domainId)?.color ?? semanticColor("coach")
}

export function getCoachRecommendations(surface: CoachSurface) {
  return coachRecommendations
    .filter((recommendation) => recommendation.surface === surface)
    .sort((a, b) => a.priority - b.priority)
}

export function getCourseForSkill(skillId: string) {
  const canonicalSkillId = normalizeSkillId(skillId)
  return courseRecommendations.find(
    (course) => normalizeSkillId(course.skillId) === canonicalSkillId,
  )
}

export function getPathwayForSkill(skillId: string) {
  const canonicalSkillId = normalizeSkillId(skillId)
  return certificatePathways.find(
    (pathway) => normalizeSkillId(pathway.skillId) === canonicalSkillId,
  )
}

export function getContact(contactId?: string) {
  return contactId ? mockContacts.find((contact) => contact.id === contactId) : undefined
}

export function getGoal(goalId?: string) {
  return goalId ? activeGoals.find((goal) => goal.id === goalId) : undefined
}

export function getCoachActionsForSkill(skillId: string) {
  const canonicalSkillId = normalizeSkillId(skillId)
  const actions = coachActions.filter((action) => action.skillId === canonicalSkillId)
  const visibleActions = actions.length > 0 ? actions : coachActions.slice(0, 4)

  return [...visibleActions]
    .sort(
      (a, b) =>
        getCoachActionTrendPriority(b, canonicalSkillId) -
        getCoachActionTrendPriority(a, canonicalSkillId),
    )
    .slice(0, 4)
}

function getCoachActionTrendPriority(action: CoachAction, skillId: string) {
  const trend = getContact(action.contactId)?.skillTrends?.find(
    (skillTrend) => normalizeSkillId(skillTrend.skillId) === normalizeSkillId(skillId),
  )

  if (!trend) return 0
  if (trend.direction === "negative") return 100 + Math.abs(trend.changePercent)
  if (trend.direction === "positive") return 50 + trend.changePercent
  return 10 + Math.abs(trend.changePercent)
}

export function getCoachAction(actionId: string) {
  const directAction = coachActions.find((action) => action.id === actionId)
  if (directAction) return directAction

  const aliasMatch = actionId.match(/^(.+)-action-(\d+)$/)
  const canonicalSkillId = normalizeSkillId(aliasMatch?.[1])
  const actionIndex = Math.max(0, Number(aliasMatch?.[2] ?? 1) - 1)

  return getCoachActionsForSkill(canonicalSkillId)[actionIndex] ?? coachActions[0]
}

export function getPrimaryCoachActionForSkill(skillId: string) {
  return getCoachActionsForSkill(skillId)[0]
}

export function getContactsForSkill(skillId: string) {
  const canonicalSkillId = normalizeSkillId(skillId)
  return mockContacts.filter((contact) =>
    contact.skillIds.some(
      (contactSkillId) => normalizeSkillId(contactSkillId) === canonicalSkillId,
    ),
  )
}

export function getSkillTrendsForContact(contactId?: string) {
  return getContact(contactId)?.skillTrends ?? []
}

export function getTopTrendForContact(contactId?: string) {
  return [...getSkillTrendsForContact(contactId)].sort(
    (a, b) => Math.abs(b.changePercent) - Math.abs(a.changePercent),
  )[0]
}

export function getContactInsight(contactId?: string) {
  const contact = getContact(contactId)
  if (!contact) return "No interaction analytics available yet."

  const trends = getSkillTrendsForContact(contact.id)
  const positiveTrend = trends
    .filter((trend) => trend.direction === "positive")
    .sort((a, b) => b.changePercent - a.changePercent)[0]
  const negativeTrend = trends
    .filter((trend) => trend.direction === "negative")
    .sort((a, b) => a.changePercent - b.changePercent)[0]

  if (positiveTrend && negativeTrend) {
    return `Your ${getSkill(positiveTrend.skillId).title} with ${contact.name} improved +${positiveTrend.changePercent}% over the last ${positiveTrend.history.length} interactions. ${getSkill(negativeTrend.skillId).title} dropped ${negativeTrend.changePercent}%; consider focusing on it next time.`
  }

  const primaryTrend = positiveTrend ?? negativeTrend ?? trends[0]
  if (!primaryTrend) return "No interactions yet. Start coaching this person to build a trend."

  const directionCopy =
    primaryTrend.direction === "positive"
      ? "is gaining strength"
      : primaryTrend.direction === "negative"
        ? "needs attention"
        : "is steady"

  return `${getSkill(primaryTrend.skillId).title} with ${contact.name} ${directionCopy} (${primaryTrend.changePercent > 0 ? "+" : ""}${primaryTrend.changePercent}%). Use the next interaction to make the trend visible.`
}

export function getContactGrowthOpportunities(limit = 3): ContactGrowthOpportunity[] {
  return mockContacts
    .flatMap((contact) =>
      (contact.skillTrends ?? []).map((trend) => ({
        contact,
        primaryTrend: trend,
        recommendation:
          trend.direction === "negative"
            ? `Repair the ${getSkill(trend.skillId).title} trend in the next conversation.`
            : `Build on the ${getSkill(trend.skillId).title} momentum while it is fresh.`,
      })),
    )
    .sort((a, b) => {
      const aPriority = a.primaryTrend.direction === "negative" ? 100 : 0
      const bPriority = b.primaryTrend.direction === "negative" ? 100 : 0
      return (
        bPriority +
        Math.abs(b.primaryTrend.changePercent) -
        (aPriority + Math.abs(a.primaryTrend.changePercent))
      )
    })
    .slice(0, limit)
}

export function getContactsForSkillWithTrends(skillId: string, limit = 3) {
  return getContactsForSkill(skillId)
    .map((contact) => ({
      contact,
      trend: contact.skillTrends?.find((skillTrend) => skillTrend.skillId === skillId),
    }))
    .filter((item): item is { contact: NetworkContact; trend: ContactSkillTrend } =>
      Boolean(item.trend),
    )
    .sort((a, b) => b.trend.changePercent - a.trend.changePercent)
    .slice(0, limit)
}

export function getContactTrendForTask(task: GrowthTask) {
  const directContact = getContact(task.contactId)
  if (directContact) {
    const directTrend =
      directContact.skillTrends?.find((trend) => task.skillIds.includes(trend.skillId)) ??
      getTopTrendForContact(directContact.id)

    if (directTrend) {
      return { contact: directContact, trend: directTrend }
    }
  }

  return mockContacts
    .flatMap((contact) =>
      (contact.skillTrends ?? [])
        .filter((trend) => task.skillIds.includes(trend.skillId))
        .map((trend) => ({ contact, trend })),
    )
    .sort((a, b) => Math.abs(b.trend.changePercent) - Math.abs(a.trend.changePercent))[0]
}

function getFallbackContactStats(contact: NetworkContact): ContactStats {
  return {
    totalInteractions: Math.max(6, contact.skillIds.length * 4),
    lastInteracted: contact.frequency === "Daily" ? "Today" : "This month",
    lastProof: "No proof yet",
    averageMood: "Neutral",
    status: contact.frequency === "Daily" ? "Active connection" : "Relationship context ready",
  }
}

function getRelatedGoalsForContact(contact: NetworkContact) {
  return activeGoals.filter((goal) =>
    goal.skillIds.some((skillId) => contact.skillIds.includes(skillId)),
  )
}

function getRelatedTasksForContact(contact: NetworkContact) {
  return tasks.filter((task) =>
    [...task.skillIds, ...task.suggestedSkillIds].some((skillId) =>
      contact.skillIds.includes(skillId),
    ),
  )
}

export function getJournalEntriesForContact(contactId?: string) {
  const contact = getContact(contactId)
  if (!contact) return []

  const directEntries = developmentJournal.filter((entry) => entry.contactIds?.includes(contact.id))
  if (directEntries.length > 0) return directEntries

  return developmentJournal
    .filter((entry) => entry.skillIds.some((skillId) => contact.skillIds.includes(skillId)))
    .slice(0, 2)
}

export function getCoachActionsForContact(contactId?: string) {
  return contactId ? coachActions.filter((action) => action.contactId === contactId) : []
}

export function getContactDetails(contactId?: string): ContactDetails | undefined {
  const contact = getContact(contactId)
  if (!contact) return undefined

  const relatedGoals = getRelatedGoalsForContact(contact).slice(0, 3)
  const relatedTasks = getRelatedTasksForContact(contact).slice(0, 3)
  const contactActions = getCoachActionsForContact(contact.id).slice(0, 4)
  const fallbackSkillActions = contact.skillIds
    .flatMap((skillId) => getCoachActionsForSkill(skillId))
    .filter((action) => action.contactId !== contact.id)
    .slice(0, Math.max(0, 4 - contactActions.length))
  const suggestedActions = [...contactActions, ...fallbackSkillActions].slice(0, 4)

  const latestConnections: ContactTimelineItem[] = [
    ...relatedGoals.slice(0, 1).map((goal) => ({
      id: `goal-${goal.id}`,
      type: "Goal" as const,
      title: goal.title,
      meta: goal.due,
      status: "Linked",
    })),
    ...relatedTasks.slice(0, 1).map((task) => ({
      id: `task-${task.id}`,
      type: "Task" as const,
      title: task.title,
      meta: task.completedAt ? `Completed ${task.completedAt}` : task.due,
      status: task.status,
    })),
    ...(contactEventsById[contact.id] ?? [
      {
        id: `event-${contact.id}-recent`,
        type: "Event" as const,
        title: `${contact.relationship} check-in`,
        meta: contactStatsById[contact.id]?.lastInteracted ?? "This month",
      },
    ]),
  ].slice(0, 4)

  return {
    contact,
    stats: contactStatsById[contact.id] ?? getFallbackContactStats(contact),
    skillTrends: getSkillTrendsForContact(contact.id),
    insight: getContactInsight(contact.id),
    practicedSkillIds: contact.skillIds,
    relatedGoals,
    relatedTasks,
    latestConnections,
    journalEntries: getJournalEntriesForContact(contact.id),
    suggestedActions: suggestedActions.map((action) => ({
      id: `contact-${contact.id}-${action.id}`,
      title: action.title,
      skillId: action.skillId,
      advice: action.quickTip,
      coachActionId: action.id,
    })),
  }
}

export function createTaskFromCoachAction(action: CoachAction): GrowthTask {
  const skill = getSkill(action.skillId)
  const relatedSkillIds =
    getDomain(skill.domainId)
      ?.skills.map((domainSkill) => domainSkill.id)
      .filter((skillId) => skillId !== skill.id)
      .slice(0, 2) ?? []

  return {
    id: `coach-task-${action.id}`,
    title: action.taskTitle,
    goal: getGoal(action.goalId)?.title ?? "Coach Mode practice",
    domainId: skill.domainId,
    skillIds: [skill.id],
    suggestedSkillIds: relatedSkillIds,
    contactId: action.contactId,
    checkpoint: `Level ${Math.min(skill.completedLevels + 1, skill.levels.length)}`,
    due: action.timing,
    status: "Active",
    nextStep: action.quickTip,
  }
}

export function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export function getTodayGrowthSummary(sourceTasks: GrowthTask[] = tasks) {
  const today = getLocalDateKey()
  const todayTasks = sourceTasks.filter((task) => task.completedAt === today)
  const skillIds = Array.from(new Set(todayTasks.flatMap((task) => task.skillIds)))

  return {
    completedCount: todayTasks.length,
    skills: getSkills(skillIds),
    nextStep:
      todayTasks[0]?.nextStep ?? "Complete one linked task to unlock today’s growth summary.",
  }
}
